import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

const API_BASE = import.meta.env.VITE_API_URL || '';

// Types
export interface LineItem {
  id?: string;
  sku: string;
  itemName?: string;
  description?: string;
  packSize?: string;
  quantity: number;
  unitPrice?: number;
  lineTotal?: number;
  flag?: string;
  note?: string;
}

export interface Order {
  id: string;
  poNumber: string;
  po_number?: string;
  customerId?: string;
  customerCode?: string;
  customer_code?: string;
  customerName: string;
  customer_name?: string;
  status: 'pending' | 'processing' | 'ready' | 'error' | 'approved' | 'rejected' | 'auto_approved' | 'Parked';
  approvalStatus?: string;
  approval_status?: string;
  orderDate: string;
  order_date?: string;
  deliveryDate?: string;
  delivery_date?: string;
  itemCount?: number;
  total?: number;
  currency?: string;
  lineItems?: LineItem[];
  items?: LineItem[];
  errorMessage?: string;
  notes?: string;
  deliverTo?: unknown;
  deliver_to?: unknown;
  unleashedOrderId?: string;
  unleashed_order_id?: string;
  unleashedOrderNumber?: string;
  unleashed_order_number?: string;
  processingLog?: Array<{
    id: string;
    timestamp: string;
    action: string;
    details: string;
    status: 'success' | 'warning' | 'error' | 'info';
  }>;
  createdAt?: string;
  created_at?: string;
  updatedAt?: string;
  updated_at?: string;
}

// Normalize order from API response (handles both camelCase and snake_case)
function normalizeOrder(order: Record<string, unknown>): Order {
  return {
    id: String(order.id),
    poNumber: String(order.po_number || order.poNumber || ''),
    customerId: String(order.customer_code || order.customerCode || ''),
    customerCode: String(order.customer_code || order.customerCode || ''),
    customerName: String(order.customer_name || order.customerName || 'Unknown'),
    status: normalizeStatus(order.approval_status || order.approvalStatus || order.status || 'pending'),
    approvalStatus: String(order.approval_status || order.approvalStatus || 'pending'),
    orderDate: String(order.order_date || order.orderDate || new Date().toISOString()),
    deliveryDate: order.delivery_date || order.deliveryDate ? String(order.delivery_date || order.deliveryDate) : undefined,
    itemCount: Array.isArray(order.items) ? order.items.length : (Number(order.itemCount) || 0),
    total: order.total ? Number(order.total) : undefined,
    currency: String(order.currency || 'GBP'),
    lineItems: Array.isArray(order.items) ? order.items.map(normalizeLineItem) : [],
    notes: order.notes ? String(order.notes) : undefined,
    deliverTo: order.deliver_to || order.deliverTo,
    unleashedOrderId: order.unleashed_order_id || order.unleashedOrderId ? String(order.unleashed_order_id || order.unleashedOrderId) : undefined,
    unleashedOrderNumber: order.unleashed_order_number || order.unleashedOrderNumber ? String(order.unleashed_order_number || order.unleashedOrderNumber) : undefined,
    createdAt: String(order.created_at || order.createdAt || new Date().toISOString()),
    updatedAt: String(order.updated_at || order.updatedAt || new Date().toISOString()),
  };
}

function normalizeLineItem(item: Record<string, unknown>): LineItem {
  return {
    id: item.id ? String(item.id) : undefined,
    sku: String(item.sku || ''),
    itemName: String(item.item_name || item.itemName || item.description || ''),
    description: String(item.description || item.item_name || item.itemName || ''),
    quantity: Number(item.quantity) || 0,
    unitPrice: item.unit_price || item.unitPrice ? Number(item.unit_price || item.unitPrice) : undefined,
    lineTotal: item.line_total || item.lineTotal ? Number(item.line_total || item.lineTotal) : undefined,
    flag: item.flag ? String(item.flag) : undefined,
    note: item.note ? String(item.note) : undefined,
  };
}

function normalizeStatus(status: unknown): Order['status'] {
  const s = String(status).toLowerCase();
  if (s === 'auto_approved' || s === 'approved') return 'approved';
  if (s === 'rejected') return 'rejected';
  if (s === 'ready') return 'ready';
  if (s === 'processing') return 'processing';
  if (s === 'error') return 'error';
  if (s === 'parked') return 'Parked';
  return 'pending';
}

// API functions
async function fetchOrders(): Promise<Order[]> {
  const response = await fetch(`${API_BASE}/api/orders?pageSize=100&sortBy=order_date&sortOrder=desc`);
  if (!response.ok) {
    throw new Error('Failed to fetch orders');
  }
  const json = await response.json();

  // Handle paginated response
  const orders = json.data || json;
  if (!Array.isArray(orders)) {
    console.warn('Unexpected orders response format:', json);
    return [];
  }

  return orders.map(normalizeOrder);
}

async function fetchOrder(id: string): Promise<Order | null> {
  const response = await fetch(`${API_BASE}/api/orders/${id}`);
  if (!response.ok) {
    if (response.status === 404) return null;
    throw new Error('Failed to fetch order');
  }
  const json = await response.json();
  const order = json.data || json;
  return normalizeOrder(order);
}

async function updateOrderStatus(data: { id: string; status: string; reason?: string }): Promise<Order> {
  const response = await fetch(`${API_BASE}/api/orders/${data.id}/approve`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status: data.status, reason: data.reason }),
  });

  if (!response.ok) {
    throw new Error('Failed to update order status');
  }

  const json = await response.json();
  return normalizeOrder(json.data || json);
}

async function deployOrder(id: string): Promise<Order> {
  const response = await fetch(`${API_BASE}/api/orders/${id}/deploy`, {
    method: 'POST',
  });

  if (!response.ok) {
    throw new Error('Failed to deploy order');
  }

  const json = await response.json();
  return normalizeOrder(json.data || json);
}

// React Query hooks
export function useOrders() {
  return useQuery({
    queryKey: ['orders'],
    queryFn: fetchOrders,
    refetchInterval: 30000, // Refetch every 30 seconds
    staleTime: 10000, // Consider data stale after 10 seconds
  });
}

export function useOrder(id: string) {
  return useQuery({
    queryKey: ['orders', id],
    queryFn: () => fetchOrder(id),
    enabled: !!id,
  });
}

export function useUpdateOrderStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateOrderStatus,
    onSuccess: (updatedOrder) => {
      // Update the orders list cache
      queryClient.setQueryData(['orders'], (oldData: Order[] | undefined) => {
        if (!oldData) return oldData;
        return oldData.map((order) =>
          order.id === updatedOrder.id ? updatedOrder : order
        );
      });

      // Update the individual order cache
      queryClient.setQueryData(['orders', updatedOrder.id], updatedOrder);

      // Invalidate to refetch fresh data
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
  });
}

export function useDeployOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deployOrder,
    onSuccess: (updatedOrder) => {
      queryClient.setQueryData(['orders', updatedOrder.id], updatedOrder);
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
  });
}
